import PropTypes from "prop-types"
import React, { Component } from "react"
import Relay from "react-relay"
import { Set } from "immutable"
import { debounce } from "lodash/function"
import { FormattedMessage, injectIntl } from "react-intl"
import classNames from "classnames"
import api from "../../api"
import sharedMessages from "../../messages/index"
import setsMessages from "../../messages/sets"
import { getStyles, getStyleTag } from "../../styles/dynamicStyles"
import { trackEvent } from "../../analyticsAndSocial"

export const SetItem = ({
  value,
  name,
  count,
  checked,
  onClick,
  favourites,
  ...props
}) => (
  <div className="styled-checkbox" {...props}>
    <input
      type="checkbox"
      id={`set-option-${value}`}
      checked={checked}
      value={value}
      onChange={e => onClick(e.target.value, e.target.checked, favourites)}
    />
    <label htmlFor={`set-option-${value}`}>
      <span className="mrt">{name}</span>
      <small className="allcaps">{`${count} item${
        count !== 1 ? "s" : ""
      }`}</small>
    </label>
  </div>
)

SetItem.propTypes = {
  onClick: PropTypes.func.isRequired,
  favourites: PropTypes.bool,
  checked: PropTypes.bool
}

SetItem.defaultProps = {
  favourites: false,
  checked: false
}

const CreateNewSet = ({ value, onSave, onChange, intl }) => (
  <form
    onSubmit={e => {
      e.preventDefault()
      onSave(value)
    }}
    className={classNames("form--can-load form--onwhite", {
      "state-form-loading": false //this.state.saving
    })}
  >
    <div className="input-group" style={getStyles("input-group")}>
      {getStyleTag(["input-group-button__button", "button--lowlight"])}
      <input
        className="input-group-field"
        type="text"
        value={value}
        placeholder={intl.formatMessage(setsMessages.createNewSet)}
        onChange={e => onChange(e.target.value)}
      />
      <div className="input-group-button">
        <button
          className="button button--lowlight"
          style={getStyles("input-group-button__button")}
          type="submit"
        >
          <FormattedMessage {...setsMessages.create} />
        </button>
      </div>
    </div>
  </form>
)

export class AddToSet extends Component {
  initSelectedSets = props => {
    props = props || this.props
    const ids = props.resource.favourite
      ? props.resource.favourite.user_set_ids.map(rowId => parseInt(rowId, 10))
      : []
    return new Set(ids)
  }

  state = {
    loading: false,
    newSetName: "",
    selectedSets: this.initSelectedSets(), // of set ids ;)
    error: null
  }

  static propTypes = {
    resource: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    onClose: PropTypes.func
  }

  componentWillReceiveProps(nextProps) {
    if (
      !this.props.resource.favourite ||
      nextProps.resource.favourite.user_set_ids.join(",") !==
        this.props.resource.favourite.user_set_ids.join(",")
    ) {
      this.setState({
        selectedSets: this.initSelectedSets(nextProps)
      })
    }
  }

  render() {
    const { newSetName, loading, error } = this.state

    const { viewer, onClose, intl } = this.props
    const recentSets = viewer.userSets.edges
      .map(edge => edge.node)
      .filter(node => !!node.id)
    const favourite = recentSets.shift()

    return (
      <div
        style={{ backgroundColor: "white" }}
        className={classNames("add-to-set", { loading: loading })}
      >
        {false && error && (
          <div className="form-message state-error">
            {getStyleTag("form-message")}
            {error.json && error.json.messages
              ? error.json.messages.join(" ")
              : error.message}
          </div>
        )}
        <fieldset>
          {!!favourite && (
            <div className="add-to-set__favourites">
              <SetItem
                checked={this.isChecked(favourite.sql_id)}
                favourites
                name={intl.formatMessage(setsMessages.favourites)}
                value={favourite.sql_id}
                count={favourite.resources.total}
                onClick={this.addToSets}
              />
            </div>
          )}
          <CreateNewSet
            value={newSetName}
            onChange={newSetName => this.setState({ newSetName })}
            onSave={name => this.createSet(name)}
            intl={intl}
          />
        </fieldset>

        <fieldset className="add-to-sets__recent">
          <legend className="add-to-sets__recent-title">
            <FormattedMessage {...setsMessages.otherSets} />
          </legend>
          <ul className="add-to-set__sets">
            {recentSets.length === 0 && (
              <li className="small mtt mbt text-center line-tight">
                <em>
                  <FormattedMessage {...setsMessages.noSets} />
                </em>
              </li>
            )}
            {recentSets
              .reverse()
              .map(({ name, resources: { total: count }, sql_id: value }) => (
                <li key={value}>
                  <SetItem
                    name={name}
                    count={count || 0}
                    value={value}
                    checked={this.isChecked(value)}
                    onClick={this.addToSets}
                  />
                </li>
              ))}
          </ul>
        </fieldset>
        {onClose && (
          <div className="popover-footer">
            <button onClick={onClose} className="button man" type="button">
              <FormattedMessage {...sharedMessages.buttonDone} />
            </button>
          </div>
        )}
      </div>
    )
  }

  isChecked = setId => {
    return this.state.selectedSets.has(setId)
  }

  createSet = name => {
    this.setState({ loading: true })

    return api("post", "user_sets", { name })
      .then(res => this.refetch())
      .then(() =>
        this.setState({
          loading: false,
          newSetName: "",
          error: null
        })
      )
      .catch(error => this.setState({ error }))
  }

  refetch = () =>
    new Promise((resolve, reject) =>
      this.props.relay.forceFetch(null, readyState => {
        if (readyState.done) resolve()
        if (readyState.error) reject(readyState.error)
      })
    )

  addToSets = (setId, added) => {
    setId = parseInt(setId, 10)
    const {
      resource: { id, type, title }
    } = this.props
    let { selectedSets } = this.state

    selectedSets = added
      ? this.state.selectedSets.add(setId)
      : this.state.selectedSets.delete(setId)
    this.setState({
      selectedSets
    })

    this.putChange(id, selectedSets)

    trackEvent(
      "resource",
      added ? "add-favourite" : "remove-favourite",
      `${type}-${id}-${title}`
    )
  }

  putChange = debounce((resourceId, selectedSets) => {
    return api("put", "user_favourites", {
      resource_id: resourceId,
      user_set_ids: selectedSets.size > 0 ? selectedSets.toJS() : []
    })
      .then(res => this.setState({ error: null }))
      .then(() => this.refetch())
      .catch(error => this.setState({ error }))
  }, 500)
}

// I need to share this with login to fix #463
export const userSetFragment = () => Relay.QL`
  fragment on UserSet {
      id
      name
      sub
      sql_id
      resources {
        total
      }
  }
`

export default Relay.createContainer(injectIntl(AddToSet), {
  fragments: {
    resource: () => Relay.QL`
      fragment on Resource {
        id
        type
        title
        favourite {
          favourite
          user_set_ids
        }
      }
    `,
    viewer: () => Relay.QL`
      fragment on Viewer {
        userSets: user_sets(first: 100) {
          edges {
            node {
              ${userSetFragment}
            }
          }
        }
      }
    `
  }
})



// WEBPACK FOOTER //
// ./webapp/components/modules/AddToSet.js